import { Suspense, lazy } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { Link } from "react-router";
import { useScopeParams } from "@/hooks/useScopeParams";
import {
  VIVO_LINK_PREFIX,
  parseVivoEntityHref,
  vivoEntityPath,
} from "@/utils/askVivoEntityLinks";

interface HighlightedCodeProps {
  language: string;
  code: string;
}

// react-syntax-highlighter's Prism build pulls in refractor and its language
// set: ~980KB that shipped in the entry chunk for every screen, though only a
// fenced code block ever renders it. Import the two leaf modules, not the
// package root or the styles index: the root also exports PrismAsyncLight,
// whose ~200 per-language dynamic imports become ~200 separate chunks, and the
// styles index re-exports all 47 themes.
const HighlightedCode = lazy(async () => {
  const [{ default: Prism }, { default: oneLight }] = await Promise.all([
    import("react-syntax-highlighter/dist/esm/prism"),
    import("react-syntax-highlighter/dist/esm/styles/prism/one-light"),
  ]);
  return {
    default: ({ language, code, ...props }: HighlightedCodeProps) => (
      <Prism style={oneLight} language={language} PreTag="div" {...props}>
        {code}
      </Prism>
    ),
  };
});

interface MarkdownRendererProps {
  content: string;
}

// Ask Vivo answers carry entity links as `vivo:subject/769-003`, and two
// separate guards drop an unknown protocol on the way to the DOM: the
// sanitizer's allow-list, and react-markdown's own url transform. Both have
// to admit the scheme — relaxing only one leaves `<a href="">`, a link that
// renders and goes nowhere. Nothing else is relaxed: `javascript:` and every
// other unlisted protocol is still stripped, and a `vivo:` target never
// reaches the browser as a URL because it is resolved to an in-app route
// below.
const schema = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), "vivo"],
  },
};

const urlTransform = (url: string) =>
  url.startsWith(VIVO_LINK_PREFIX) ? url : defaultUrlTransform(url);

const MarkdownRenderer = ({ content }: MarkdownRendererProps) => {
  const { buildUrl, scope } = useScopeParams();

  return (
    <ReactMarkdown
      rehypePlugins={[[rehypeSanitize, schema]]}
      remarkPlugins={[remarkGfm]}
      urlTransform={urlTransform}
      components={{
        code({
          className,
          children,
          ...props
        }: React.HTMLAttributes<HTMLElement> & {
          node?: import("hast").Element;
        }) {
          const match = /language-(\w+)/.exec(className || "");
          const code = String(children).replace(/\n$/, "");
          return match ? (
            <div className="code-block-container">
              <Suspense
                fallback={
                  <pre>
                    <code className={className}>{code}</code>
                  </pre>
                }
              >
                <HighlightedCode language={match[1]} code={code} {...props} />
              </Suspense>
            </div>
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },

        a({
          href,
          children,
          ...props
        }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
          const entity = parseVivoEntityHref(href);
          if (entity) {
            const path = vivoEntityPath(entity, scope);
            if (!path) return <>{children}</>;
            return (
              <Link to={buildUrl(path)} className="vivo-entity-link" {...props}>
                {children}
              </Link>
            );
          }

          const processedHref =
            href && href.startsWith("/wiki/") ? buildUrl(href.slice(1)) : href;
          return (
            <a href={processedHref} {...props}>
              {children}
            </a>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
